import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../../../services/api';
import { FiEdit, FiAlertTriangle, FiTrash2 } from 'react-icons/fi';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';
import PermissionButton from '../../../components/PermissionButton';
import { getAuth } from '../../../utils/auth';

const UserView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userData: currentUser } = getAuth();
  
  // Check if the user is viewing themselves
  const isViewingSelf = currentUser?.id === id;

  // Fetch user details
  const { data: user, isLoading, isError, error } = useQuery(
    ['admin-user', id],
    () => adminAPI.getUser(id),
    {
      enabled: !!id
    }
  );

  // Toggle user status mutation  
  const toggleStatusMutation = useMutation(
    ({ activate }) => 
      activate ? adminAPI.activateUser(id) : adminAPI.deactivateUser(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-user', id]);
        queryClient.invalidateQueries('admin-users');
      }
    }
  );

  // Delete user mutation
  const deleteUserMutation = useMutation(
    () => adminAPI.deleteUser(id),
    {
      onSuccess: () => {
        navigate('/admin/users');
      }
    }
  );

  // Helper function to get user's primary role
  const getUserRole = (user) => {
    return user?.roles?.[0]?.name || 'No role assigned';
  };

  // Helper function to format role display name
  const formatRoleName = (role) => {
    if (role === 'No role assigned') return role;
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error?.message || 'Error loading user details'} />;
  }

  const handleToggleStatus = async () => {
    const action = user?.is_active ? 'deactivate' : 'activate';
    if (window.confirm(`Are you sure you want to ${action} this user?`)) {
      await toggleStatusMutation.mutateAsync({ activate: !user?.is_active });
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to permanently delete this user? This action cannot be undone.')) {
      await deleteUserMutation.mutateAsync();
    }
  };

  const userRole = getUserRole(user);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Internal User Details</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">User information and account details.</p>
          </div>
          <div className="flex space-x-3">
            <PermissionButton
              permissions="manage_users"
              onClick={() => navigate(`/admin/users/${id}/edit`)}
              className="border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-indigo-500"
            >
              <FiEdit className="mr-2 h-4 w-4" />
              Edit
            </PermissionButton>
            
            <PermissionButton
              permissions="manage_users"
              onClick={handleToggleStatus}
              disabled={toggleStatusMutation.isLoading || isViewingSelf}
              className={`border shadow-sm ${
                user?.is_active 
                  ? 'border-red-300 text-red-700 bg-white hover:bg-red-50 focus:ring-red-500'
                  : 'border-green-300 text-green-700 bg-white hover:bg-green-50 focus:ring-green-500'
              }`}
              title={isViewingSelf ? "You cannot deactivate your own account" : ""}
            >
              <FiAlertTriangle className="mr-2 h-4 w-4" />
              {user?.is_active ? 'Deactivate User' : 'Activate User'}
            </PermissionButton>
            
            {!user?.is_active && (
              <PermissionButton
                permissions="delete_users"
                onClick={handleDelete}
                disabled={deleteUserMutation.isLoading || isViewingSelf}
                className="border-red-300 text-red-700 bg-white hover:bg-red-50 focus:ring-red-500"
                title={isViewingSelf ? "You cannot delete your own account" : ""}
              >
                <FiTrash2 className="mr-2 h-4 w-4" />
                Delete User
              </PermissionButton>
            )}
          </div>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Full name</dt>
              <dd className="mt-1 text-sm text-gray-900">{user?.name}</dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="mt-1 text-sm text-gray-900">{user?.email}</dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Role</dt>
              <dd className="mt-1 text-sm text-gray-900">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  userRole === 'system_admin' ? 'bg-purple-100 text-purple-800' :
                  userRole === 'manager' ? 'bg-blue-100 text-blue-800' :
                  userRole === 'warehouse' ? 'bg-orange-100 text-orange-800' :
                  userRole === 'accounting' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {formatRoleName(userRole)}
                </span>
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1 text-sm text-gray-900">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user?.is_active 
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {user?.is_active ? 'Active' : 'Inactive'}
                </span>
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">User Type</dt>
              <dd className="mt-1 text-sm text-gray-900">
                <span className="capitalize">{user?.userType || 'Internal'}</span>
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Account Created</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
              </dd>
            </div>
            {user?.CustomerId && (
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Associated Customer</dt>
                <dd className="mt-1 text-sm text-gray-900">{user?.Customer?.companyName || 'N/A'}</dd>
              </div>
            )}
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {user?.updatedAt ? new Date(user.updatedAt).toLocaleString() : 'N/A'}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

export default UserView; 